import React, { useState, useMemo } from 'react';
import {
    Box,
    Chip,
    IconButton,
    Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from 'prop-types';
import {
    hasActualPortfolioPriceGap,
} from '../utils/marketDataStatus';
import {
    READINESS_STATUS_EXCLUDED,
    READINESS_STATUS_FAILED,
    READINESS_STATUS_PENDING,
    getReadinessStatus,
} from '../utils/positionReadiness';
import {
    securityCountry,
    securityIdentityDetails,
    securityIdentityPresentation,
} from '../utils/securityIdentityPresentation';
import { negativeCell } from '../styles/tailwindClasses';
import { compactIconButtonSx } from './pos_sizing_table/tableStyles';
import SecurityDetailsPopover from './security_details/SecurityDetailsPopover';
import {
    formatRiskOverviewPositionLocalPrice,
} from '../utils/riskOverviewPositionPresentation';
import RiskWorkbookExportButton from './risk_overview/RiskWorkbookExportButton';
import CompactPanelHeader from './commonComponents/CompactPanelHeader';
import { CountryFlagText } from './shared/securityDisplayPrimitives';
import {
    SecurityIdentityFieldMenu,
    securityIdentityFieldLabel,
} from './commonComponents/SecurityIdentityFieldSelector';
import DataTable from './data_table/DataTable';
import ColumnVisibilityMenu from './data_table/ColumnVisibilityMenu';
import { sortByNumber, sortByText } from './data_table/sorting';
import { optimizedCellClassName } from './data_table/tableClasses';
import { cx } from './ui/classNames';
import Skeleton from './ui/Skeleton';

const PINNED_COLUMNS = Object.freeze(['identity_summary']);
const EMPTY_POSITION_ROW_COUNT = 40;
const EMPTY_MARKET_ROWS = Object.freeze([]);
// Positions without a stored price keep the info tint (and a blue hover).
const NO_PRICE_ROW_CLASS_NAME = '!bg-ui-info-bg hover:!bg-ui-selected';

const isBlank = (value) => value === null || value === undefined || Number.isNaN(Number(value));
const isNegative = (value) => !isBlank(value) && Number(value) < 0;

// Number formats, '-' for a missing value.
const formatPrice = (value) => (isBlank(value) ? '-' : Number(value).toFixed(2));
const formatWhole = (value) => (
    isBlank(value) ? '-' : Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })
);
const formatRounded = (value) => (isBlank(value) ? '-' : Math.round(Number(value)).toLocaleString());
// Quantities as the browser formats them; other values as they are.
const formatQuantity = (value) => (
    typeof value === 'number' && !Number.isNaN(value) ? value.toLocaleString() : (value || '')
);
// Optimized (OPT) values, up to two decimals.
const formatOptimized = (value) => (
    isBlank(value) ? '-' : Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })
);

// A right-aligned number column; negative values are red and optimized
// columns carry the optimized tint. Placeholder rows stay blank.
function numberColumn({ id, header, size, format, optimized = false }) {
    return {
        id,
        header,
        size,
        accessorFn: (row) => row[id],
        sortFn: sortByNumber,
        cell: ({ row, getValue }) => (row.original._isPlaceholder ? null : format(getValue())),
        meta: {
            align: 'right',
            cellClassName: ({ getValue }) => cx(
                optimized && optimizedCellClassName,
                isNegative(getValue()) && negativeCell,
            ),
        },
    };
}

function getRowClassName(row) {
    if (row._isPlaceholder) return 'pointer-events-none';
    return hasActualPortfolioPriceGap(row) ? NO_PRICE_ROW_CLASS_NAME : undefined;
}

function PositionDetailsTable({
    holdings,
    positionPresentation,
    priceSyncLoading = false,
    marketDataRows = EMPTY_MARKET_ROWS,
    directExportEnabled,
    isLoading = false,
    optimizedLoading = false,
    onOpenPortfolioEditor = null,
}) {
    const [columnVisibility, setColumnVisibility] = useState({});
    const [identityDisplayField, setIdentityDisplayField] = useState('company_name');
    const marketDataByIdentity = useMemo(() => new Map(marketDataRows.filter((row) => row.cognive_id).map((row) => [row.cognive_id, row])), [marketDataRows]);

    const {
        rows,
        exportRows,
        attributeColumns = [],
        diagnostics,
    } = positionPresentation;
    const isInitialLoading = isLoading && rows.length === 0;
    const exportSheets = useMemo(() => Object.freeze([Object.freeze({
        name: 'Positions',
        kind: 'json',
        rows: exportRows,
    })]), [exportRows]);
    const noPriceCount = useMemo(
        () => (holdings || []).filter((row) => hasActualPortfolioPriceGap(row)).length,
        [holdings],
    );
    const readinessCounts = useMemo(
        () => (holdings || []).reduce((counts, row) => {
            const status = getReadinessStatus(row);
            if (Object.prototype.hasOwnProperty.call(counts, status)) {
                counts[status] += 1;
            }
            return counts;
        }, {
            [READINESS_STATUS_PENDING]: 0,
            [READINESS_STATUS_FAILED]: 0,
            [READINESS_STATUS_EXCLUDED]: 0,
        }),
        [holdings],
    );
    const unjoinableComparisonCount = diagnostics.actualUnjoinableCount
        + diagnostics.optimizedUnjoinableCount;

    // Identity, country and the Watchlist attribute columns always show;
    // the others can be hidden from the Columns menu.
    const columns = useMemo(() => {
        // Country has its own column, as in the Watchlist.
        const gridAttributeColumns = attributeColumns.filter(
            ({ isSystemCountry }) => !isSystemCountry,
        );
        return [
            {
                id: 'identity_summary',
                header: securityIdentityFieldLabel(identityDisplayField),
                size: 250,
                accessorFn: (row) => securityIdentityPresentation(row, identityDisplayField).value,
                sortFn: sortByText,
                enableHiding: false,
                cell: ({ row: { original } }) => {
                    if (original._isPlaceholder) return null;
                    const presentation = securityIdentityPresentation(original, identityDisplayField);
                    return (
                        <span
                            className={cx(
                                'block min-w-0',
                                identityDisplayField === 'submitted_identifier' && '[font-family:monospace]',
                            )}
                        >
                            <SecurityDetailsPopover
                                row={{ ...original, ...marketDataByIdentity.get(original.cognive_id) }}
                                loading={Boolean(priceSyncLoading)}
                                displayLabel={presentation.value}
                                labelTooltip={presentation.tooltip}
                                identityDetails={securityIdentityDetails(original)}
                                triggerAriaLabel={`Security and market data details for ${presentation.value}`}
                            />
                        </span>
                    );
                },
                meta: {
                    headerControls: () => (
                        <SecurityIdentityFieldMenu
                            value={identityDisplayField}
                            onChange={setIdentityDisplayField}
                        />
                    ),
                },
            },
            {
                id: 'country',
                header: 'Country',
                size: 105,
                accessorFn: (row) => securityCountry(row),
                sortFn: sortByText,
                enableHiding: false,
                cell: ({ row, getValue }) => (
                    row.original._isPlaceholder ? null : <CountryFlagText country={getValue()} />
                ),
            },
            numberColumn({ id: 'price', header: 'Price ($)', size: 118, format: formatPrice }),
            {
                id: 'local_price',
                header: 'Local Price ($)',
                size: 150,
                accessorFn: (row) => row.local_price,
                enableSorting: false,
                cell: ({ row: { original } }) => (
                    original._isPlaceholder
                        ? null
                        : formatRiskOverviewPositionLocalPrice(original.local_price, original.local_currency)
                ),
            },
            numberColumn({ id: 'quantity', header: 'Quantity', size: 100, format: formatQuantity }),
            numberColumn({
                id: 'opt_quantity', header: 'Quantity OPT', size: 120, format: formatOptimized, optimized: true,
            }),
            numberColumn({ id: 'nmv', header: 'GMV', size: 100, format: formatWhole }),
            numberColumn({
                id: 'opt_nmv', header: 'GMV OPT', size: 100, format: formatOptimized, optimized: true,
            }),
            ...gridAttributeColumns.map(({ displayName, field }) => ({
                id: field,
                header: displayName,
                size: Math.min(220, Math.max(120, displayName.length * 9 + 36)),
                accessorFn: (row) => row?.[field] ?? '',
                sortFn: sortByText,
                enableHiding: false,
                meta: { description: `Watchlist attribute ${displayName}` },
            })),
            // Matches holding.idioRiskDollar
            numberColumn({ id: 'idioRiskDollar', header: 'Idio Risk ($)', size: 120, format: formatRounded }),
            numberColumn({
                id: 'opt_idioRiskDollar',
                header: 'Idio Risk OPT ($)',
                size: 140,
                format: formatOptimized,
                optimized: true,
            }),
            numberColumn({ id: 'factorRiskDollar', header: 'Factor Risk ($)', size: 130, format: formatRounded }),
            numberColumn({
                id: 'opt_factorRiskDollar',
                header: 'Factor Risk OPT ($)',
                size: 160,
                format: formatOptimized,
                optimized: true,
            }),
            numberColumn({ id: 'mcfr_bp', header: 'MCFR (bp)', size: 110, format: formatRounded }),
            numberColumn({
                id: 'opt_mcfr_bp', header: 'MCFR OPT(bp)', size: 120, format: formatOptimized, optimized: true,
            }),
            numberColumn({ id: 'mctr_bp', header: 'MCTR (bp)', size: 110, format: formatRounded }),
            numberColumn({
                id: 'opt_mctr_bp', header: 'MCTR OPT (bp)', size: 120, format: formatOptimized, optimized: true,
            }),
            numberColumn({ id: 'mcsr_bp', header: 'MCSR (bp)', size: 110, format: formatRounded }),
            numberColumn({
                id: 'opt_mcsr_bp', header: 'MCSR OPT (bp)', size: 160, format: formatOptimized, optimized: true,
            }),
        ].map((column) => {
            const loading = column.id.startsWith('opt_')
                ? optimizedLoading : isLoading && column.sortFn === sortByNumber;
            return loading ? {
                ...column,
                cell: ({ row }) => (row.original._isPlaceholder
                    ? null : <Skeleton width="70%" className="ml-auto" />),
            } : column;
        });
    }, [
        attributeColumns,
        identityDisplayField,
        marketDataByIdentity,
        priceSyncLoading,
        isLoading,
        optimizedLoading,
    ]);

    const gridRows = useMemo(() => {
        if (rows.length > 0) {
            return rows;
        }
        return Array.from({ length: EMPTY_POSITION_ROW_COUNT }, (_, index) => ({
            id: `position-empty-${index}`,
            _isPlaceholder: true,
        }));
    }, [rows]);

    return (
      
            <>
            <CompactPanelHeader
                title="Position Details"
                leading={(
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexWrap: 'wrap',
                            '& .MuiChip-root': { height: 20, fontSize: '0.68rem' },
                        }}
                    >
                        {noPriceCount > 0 && (
                            <Tooltip
                                title="These positions are still present, but stored price coverage is not currently available for them."
                                arrow
                            >
                                <Chip
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    label={`${noPriceCount} no price`}
                                />
                            </Tooltip>
                        )}
                        {readinessCounts.pending > 0 && (
                            <Tooltip
                                title="These positions are still present and may become eligible when required data arrives."
                                arrow
                            >
                                <Chip
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    label={`${readinessCounts.pending} pending`}
                                />
                            </Tooltip>
                        )}
                        {readinessCounts.failed > 0 && (
                            <Chip
                                size="small"
                                color="error"
                                variant="outlined"
                                label={`${readinessCounts.failed} failed`}
                            />
                        )}
                        {readinessCounts.excluded > 0 && (
                            <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                label={`${readinessCounts.excluded} excluded`}
                            />
                        )}
                        {unjoinableComparisonCount > 0 && (
                            <Tooltip
                                title="These rows are shown separately because they do not have canonical Cognive identity and cannot be compared safely."
                                arrow
                            >
                                <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label={`${unjoinableComparisonCount} unjoinable`}
                                />
                            </Tooltip>
                        )}
                    </Box>
                )}
            >
                <ColumnVisibilityMenu
                    columns={columns}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={setColumnVisibility}
                />
                <Tooltip title="Add watchlist securities">
                    <span>
                        <IconButton
                            size="small"
                            aria-label="Add watchlist securities"
                            onClick={onOpenPortfolioEditor}
                            disabled={!onOpenPortfolioEditor}
                            sx={compactIconButtonSx}
                        >
                            <AddIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <RiskWorkbookExportButton
                    tooltip="Download position details"
                    ariaLabel="Download position details"
                    fileName="positions.xlsx"
                    sheets={exportSheets}
                    disabled={!directExportEnabled || isInitialLoading || rows.length === 0}
                    sx={compactIconButtonSx}
                />
            </CompactPanelHeader>

            <Box
                aria-busy={isLoading || optimizedLoading || undefined}
                aria-label={isInitialLoading ? 'Loading position details' : undefined}
                sx={{ width: '100%' }}
            >
                <DataTable
                    aria-label="Position details"
                    data={gridRows}
                    columns={columns}
                    pinnedColumns={PINNED_COLUMNS}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={setColumnVisibility}
                    getRowClassName={getRowClassName}
                    loading={isInitialLoading}
                    height={500}
                    virtualize
                />
            </Box>
            </>
    );
}

PositionDetailsTable.propTypes = {
    holdings: PropTypes.arrayOf(
        PropTypes.shape({
            ticker: PropTypes.string.isRequired,
            quantity: PropTypes.number,
            nmv: PropTypes.number,
            idioVol: PropTypes.number,
            idioRiskDollar: PropTypes.number,
            idioVarPct: PropTypes.number,
            factorVarPct: PropTypes.number,
            factorVol: PropTypes.number,
            factorRiskDollar: PropTypes.number,
            mcfr_bp: PropTypes.number,
            mctr_bp: PropTypes.number,
            mcsr_bp: PropTypes.number,
        })
    ).isRequired,
    positionPresentation: PropTypes.shape({
        rows: PropTypes.array.isRequired,
        exportRows: PropTypes.array.isRequired,
        attributeColumns: PropTypes.arrayOf(PropTypes.shape({
            definitionId: PropTypes.string.isRequired,
            displayName: PropTypes.string.isRequired,
            field: PropTypes.string.isRequired,
            isSystemCountry: PropTypes.bool,
        })).isRequired,
        diagnostics: PropTypes.shape({
            actualUnjoinableCount: PropTypes.number.isRequired,
            optimizedUnjoinableCount: PropTypes.number.isRequired,
        }).isRequired,
    }).isRequired,
    directExportEnabled: PropTypes.bool.isRequired,
    priceSyncLoading: PropTypes.bool,
    marketDataRows: PropTypes.arrayOf(PropTypes.object),
    isLoading: PropTypes.bool,
    optimizedLoading: PropTypes.bool,
    onOpenPortfolioEditor: PropTypes.func,
};

export default PositionDetailsTable;
